/** @jsxImportSource @emotion/react */
import React, { useMemo, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Descriptions,
  Empty,
  Form,
  InputNumber,
  Row,
  Select,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { InfoCircleOutlined, PushpinFilled, PushpinOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import type { EChartsOption } from 'echarts';
import MainLayout from '../../components/common/MainLayout';
import EChart from '../../components/charts/EChart';
import ScenarioEconomyPanel from '../../components/admin/ScenarioEconomyPanel';
import ScenarioEconomyComposer, {
  toEconomyCommand,
  type CommandDraft,
} from '../../components/admin/ScenarioEconomyComposer';
import {
  useGetDebugTemplatesQuery,
  useRunDebugScenarioMutation,
  type DebugDay,
  type DebugDayPlanting,
  type DebugEvent,
  type DebugRunResponse,
  type ScenarioDiagnostic,
  type ScenarioTemplate,
} from '../../api/seasonsAdminApi';

const { Text } = Typography;

const ACTION_OPTIONS = [
  { value: 'watering', label: 'Полив' },
  { value: 'fertilizing', label: 'Подкормка' },
  { value: 'harvest', label: 'Сбор' },
];

const FERTILIZER_KINDS = [
  { value: '', label: 'NPK 16-16-16 (дефолт)' },
  { value: 'urea', label: 'Мочевина 46-0-0 (fast-N, вымывается)' },
  { value: 'compost_mature', label: 'Зрелый компост (slow, неделями)' },
  { value: 'manure_aged', label: 'Перепревший навоз' },
  { value: 'bone_meal', label: 'Костная мука (P)' },
  { value: 'wood_ash', label: 'Зола (K)' },
];

/** Палитра проверена на различимость при дальтонизме (CVD ΔE ≥ 8 в парах одного графика). */
const CHART_COLORS = {
  height: '#2a78d6',
  gdd: '#eb6834',
  health: '#2a78d6',
  moisture: '#1baf7a',
  precip: '#4a3aa7',
  unripe: '#008300',
  ripe: '#eda100',
  collected: '#4a3aa7',
  event: '#8c8c8c',
};

const BETTER = '#389e0d';
const WORSE = '#cf1322';

const ACTION_BUDGET_TRAP = 'Ограничение actionCount у цели проверяется «живьём» на каждом действии, но НЕ на завершении сценария: цель с бюджетом действий может показать FAIL здесь и PASS в сохранённом счёте сезона.';

const DIAGNOSTIC_HINTS: Record<string, string> = {
  scenario_event_target_not_found: 'Событие указало на несуществующий id — реплей молча его пропустил, и прогон выглядит так, будто действие ничего не сделало.',
  weather_unavailable: 'На день не нашлось погоды — движок взял фолбэк, траектория может расходиться с ожидаемой.',
  planting_died: 'Посадка погибла — дальнейшие метрики роста заморожены.',
  scenario_world_spec_invalid: 'worldSpec шаблона не прошёл валидацию — мир собран частично.',
};

const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00.000Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

interface DraftEvent {
  key: string;
  day: number;
  action: string;
  targetId: string;
  depthMm?: number;
  fertilizerKind?: string;
  quantityKg?: number;
}

interface EventMark {
  date: string;
  label: string;
}

interface PinnedRun {
  run: DebugRunResponse;
  label: string;
}

const toDebugEvent = (draft: DraftEvent, template: ScenarioTemplate): DebugEvent => {
  const isPlantingTarget = draft.action === 'harvest';
  const payload: Record<string, unknown> = {};
  if (draft.action === 'watering' && draft.depthMm) payload.wateringDepthMm = draft.depthMm;
  if (draft.action === 'fertilizing' && draft.fertilizerKind) payload.fertilizerKind = draft.fertilizerKind;
  if (draft.action === 'harvest' && draft.quantityKg) payload.quantityKg = draft.quantityKg;
  return {
    id: draft.key,
    type: draft.action,
    date: addDays(template.worldSpec.startDate, draft.day),
    target: isPlantingTarget ? { plantingId: draft.targetId } : { bedId: draft.targetId },
    payload,
  };
};

const phaseTag = (phase?: string) => {
  const colors: Record<string, string> = {
    germination: 'default',
    seedling: 'lime',
    vegetative: 'green',
    flowering: 'purple',
    fruiting: 'volcano',
    harvest_ready: 'gold',
    senescence: 'default',
  };
  if (!phase) return <Text type="secondary">—</Text>;
  return <Tag color={colors[phase] || 'default'}>{phase}</Tag>;
};

const num = (value: unknown, digits = 1) => (typeof value === 'number' && Number.isFinite(value)
  ? value.toFixed(digits)
  : '—');

/** Пропуск данных — это null (разрыв линии), а не 0: ноль был бы враньём. */
const chartValue = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value)
  ? value
  : null);

const lastDay = (run: DebugRunResponse): DebugDay | undefined => run.days[run.days.length - 1];

const finalPlanting = (run: DebugRunResponse, plantingId?: string): DebugDayPlanting | undefined => (
  plantingId ? lastDay(run)?.plantings?.[plantingId] : undefined
);

const eventMarkLine = (marks: EventMark[]) => ({
  silent: true,
  symbol: ['none', 'none'],
  data: marks.map((mark) => ({
    xAxis: mark.date,
    lineStyle: { color: CHART_COLORS.event, type: 'dashed' as const, width: 1 },
    label: { show: true, formatter: mark.label, fontSize: 10, color: CHART_COLORS.event },
  })),
});

const severityToAlertType = (severity: ScenarioDiagnostic['severity']): 'error' | 'warning' | 'info' => {
  if (severity === 'error') return 'error';
  if (severity === 'warning') return 'warning';
  return 'info';
};

// ── Диагностика реплея ─────────────────────────────────────────────────────

const DiagnosticsPanel: React.FC<{ diagnostics?: ScenarioDiagnostic[] }> = ({ diagnostics }) => {
  if (!diagnostics || diagnostics.length === 0) return null;
  return (
    <Space direction="vertical" size={8} style={{ width: '100%' }}>
      {diagnostics.map((diagnostic, index) => {
        const details: string[] = [];
        if (diagnostic.date) details.push(`дата ${diagnostic.date}`);
        if (typeof diagnostic.tick === 'number') details.push(`тик ${diagnostic.tick}`);
        if (diagnostic.target?.id) {
          details.push(`цель ${diagnostic.target.kind ? `${diagnostic.target.kind}:` : ''}${diagnostic.target.id}`);
        }
        if (diagnostic.event_id) details.push(`событие ${diagnostic.event_id}`);
        const hint = DIAGNOSTIC_HINTS[diagnostic.code];
        return (
          <Alert
            key={`${diagnostic.code}-${diagnostic.event_id || diagnostic.date || ''}-${index}`}
            type={severityToAlertType(diagnostic.severity)}
            showIcon
            message={(
              <Space wrap size={6}>
                <Tag color={diagnostic.severity === 'error' ? 'red' : diagnostic.severity === 'warning' ? 'orange' : 'blue'}>
                  {diagnostic.code}
                </Tag>
                <Text>{diagnostic.message || hint || '—'}</Text>
              </Space>
            )}
            description={(details.length > 0 || (hint && diagnostic.message)) ? (
              <Space direction="vertical" size={2}>
                {hint && diagnostic.message ? <Text type="secondary">{hint}</Text> : null}
                {details.length > 0 ? <Text type="secondary">{details.join(' · ')}</Text> : null}
              </Space>
            ) : undefined}
          />
        );
      })}
    </Space>
  );
};

// ── Цели ───────────────────────────────────────────────────────────────────

const GoalsPanel: React.FC<{
  result: DebugRunResponse;
  template: ScenarioTemplate | null;
  extra?: React.ReactNode;
}> = ({ result, template, extra }) => {
  const progress = result.goalsProgress;
  const budgetGoalIds = useMemo(() => new Set(
    (template?.goals || []).filter((goal) => goal.actionCount).map((goal) => goal.id),
  ), [template]);

  return (
    <Card
      size="small"
      extra={extra}
      title={(
        <Space wrap>
          Цели
          <Tag color={progress.scoreTier === 3 ? 'blue' : progress.scoreTier === 2 ? 'red' : progress.scoreTier === 1 ? 'default' : 'orange'}>
            {progress.scoreTier ? `tier ${progress.scoreTier}` : 'без ленты'}
          </Tag>
          <Text type="secondary">{progress.passedCount}/{progress.total}</Text>
          {progress.requiredTotal != null && progress.requiredTotal > 0 && (
            <Tooltip title="Обязательные цели: пока они не выполнены, лента не выдаётся вовсе — сколько бы бонусов ни набралось.">
              <Tag color={progress.requiredSatisfied ? 'green' : 'volcano'}>
                обязательные {progress.requiredPassed}/{progress.requiredTotal}
              </Tag>
            </Tooltip>
          )}
          <Text type="secondary">
            {result.terminal ? 'терминал' : `курсор ${result.cursorDate}`} · {result.daysAdvanced} д
          </Text>
        </Space>
      )}
    >
      {progress.requiredSatisfied === false && progress.scoreTier === null && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="Ленты нет: не выполнены обязательные цели"
          description="Подсчёт числа выполненных целей сам по себе не правило успеха — иначе ленту можно было бы получить за бюджет и здоровье, вообще не вырастив то, ради чего задание написано."
        />
      )}
      {budgetGoalIds.size > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message="В сценарии есть цель с бюджетом действий"
          description={ACTION_BUDGET_TRAP}
        />
      )}
      <Table
        rowKey={(g) => g.id}
        size="small"
        pagination={false}
        dataSource={progress.goals}
        columns={[
          {
            title: 'Цель',
            dataIndex: 'label',
            render: (label: string, g) => (
              <Space size={6}>
                <Text>{label}</Text>
                {g.required === true && <Tag color="volcano">обязательная</Tag>}
                {g.required === false && <Tag>бонус</Tag>}
                {budgetGoalIds.has(g.id) && (
                  <Tooltip title={ACTION_BUDGET_TRAP}>
                    <Tag color="orange">бюджет действий</Tag>
                  </Tooltip>
                )}
              </Space>
            ),
          },
          {
            title: 'Значение',
            dataIndex: 'value',
            render: (v: unknown) => (typeof v === 'number' ? v.toFixed(2) : String(v ?? '—')),
          },
          {
            title: 'Условие',
            key: 'cond',
            render: (_, g) => {
              const target = typeof g.target === 'object' && g.target !== null
                ? `${(g.target as { min?: number }).min}–${(g.target as { max?: number }).max}`
                : String(g.target);
              const ops: Record<string, string> = { gte: '≥', lte: '≤', gt: '>', lt: '<', eq: '=', between: '∈' };
              return <Text>{ops[g.operator] || g.operator} {target}</Text>;
            },
          },
          {
            title: (
              <Space size={4}>
                Вердикт
                <Tooltip title={ACTION_BUDGET_TRAP}>
                  <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            ),
            dataIndex: 'passed',
            width: 110,
            render: (passed: boolean) => (passed ? <Tag color="green">PASS</Tag> : <Tag color="red">FAIL</Tag>),
          },
        ]}
      />
    </Card>
  );
};

// ── Графики траектории ─────────────────────────────────────────────────────

const TrajectoryCharts: React.FC<{
  days: DebugDay[];
  plantingId?: string;
  bedId?: string;
  eventMarks: EventMark[];
}> = ({ days, plantingId, bedId, eventMarks }) => {
  const dates = useMemo(() => days.map((day) => day.simDate), [days]);
  const marks = useMemo(() => {
    const known = new Set(dates);
    return eventMarks.filter((mark) => known.has(mark.date));
  }, [eventMarks, dates]);

  const growthOption = useMemo<EChartsOption>(() => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: { data: ['Высота, см', 'Накопленный GDD'], top: 0 },
    grid: { left: 55, right: 60, top: 40, bottom: 55 },
    xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: [
      { type: 'value', name: 'см', scale: true },
      { type: 'value', name: 'GDD', position: 'right', scale: true, splitLine: { show: false } },
    ],
    series: [
      {
        name: 'Высота, см',
        type: 'line',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.height, width: 2 },
        itemStyle: { color: CHART_COLORS.height },
        data: days.map((day) => chartValue(plantingId ? day.plantings?.[plantingId]?.height : undefined)),
        markLine: marks.length > 0 ? eventMarkLine(marks) : undefined,
      },
      {
        name: 'Накопленный GDD',
        type: 'line',
        yAxisIndex: 1,
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.gdd, width: 2 },
        itemStyle: { color: CHART_COLORS.gdd },
        data: days.map((day) => chartValue(plantingId ? day.plantings?.[plantingId]?.accumulatedGDD : undefined)),
      },
    ],
  }), [days, dates, marks, plantingId]);

  const stressOption = useMemo<EChartsOption>(() => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: { data: ['Здоровье', 'Влага грядки, %', 'Осадки, мм'], top: 0 },
    grid: { left: 55, right: 60, top: 40, bottom: 55 },
    xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: [
      { type: 'value', name: '%', min: 0, max: 100 },
      { type: 'value', name: 'мм', position: 'right', min: 0, splitLine: { show: false } },
    ],
    series: [
      {
        name: 'Осадки, мм',
        type: 'bar',
        yAxisIndex: 1,
        barMaxWidth: 10,
        itemStyle: { color: CHART_COLORS.precip, opacity: 0.5, borderRadius: [4, 4, 0, 0] },
        data: days.map((day) => chartValue(day.weather?.precipitationMm)),
      },
      {
        name: 'Здоровье',
        type: 'line',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.health, width: 2 },
        itemStyle: { color: CHART_COLORS.health },
        data: days.map((day) => chartValue(plantingId ? day.plantings?.[plantingId]?.healthScore : undefined)),
        markLine: marks.length > 0 ? eventMarkLine(marks) : undefined,
      },
      {
        name: 'Влага грядки, %',
        type: 'line',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.moisture, width: 2 },
        itemStyle: { color: CHART_COLORS.moisture },
        data: days.map((day) => chartValue(bedId ? day.beds?.[bedId]?.moisturePct : undefined)),
      },
    ],
  }), [days, dates, marks, plantingId, bedId]);

  const yieldOption = useMemo<EChartsOption>(() => ({
    tooltip: { trigger: 'axis', axisPointer: { type: 'cross' } },
    legend: { data: ['Зелёное, кг', 'Спелое, кг', 'Собрано (накопительно), кг'], top: 0 },
    grid: { left: 55, right: 60, top: 40, bottom: 55 },
    xAxis: { type: 'category', data: dates, axisLabel: { rotate: 45, fontSize: 10 } },
    yAxis: { type: 'value', name: 'кг', min: 0 },
    series: [
      {
        name: 'Зелёное, кг',
        type: 'line',
        stack: 'fruit',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.unripe, width: 2 },
        itemStyle: { color: CHART_COLORS.unripe },
        areaStyle: { color: CHART_COLORS.unripe, opacity: 0.25 },
        data: days.map((day) => chartValue(plantingId ? day.plantings?.[plantingId]?.fruitUnripeKg : undefined)),
        markLine: marks.length > 0 ? eventMarkLine(marks) : undefined,
      },
      {
        name: 'Спелое, кг',
        type: 'line',
        stack: 'fruit',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.ripe, width: 2 },
        itemStyle: { color: CHART_COLORS.ripe },
        areaStyle: { color: CHART_COLORS.ripe, opacity: 0.35 },
        data: days.map((day) => chartValue(plantingId ? day.plantings?.[plantingId]?.fruitRipeKg : undefined)),
      },
      {
        name: 'Собрано (накопительно), кг',
        type: 'line',
        symbol: 'none',
        connectNulls: false,
        lineStyle: { color: CHART_COLORS.collected, width: 2, type: 'dashed' },
        itemStyle: { color: CHART_COLORS.collected },
        data: days.map((day) => chartValue(
          plantingId ? day.plantings?.[plantingId]?.cumulativeHarvestCollectedKg : undefined,
        )),
      },
    ],
  }), [days, dates, marks, plantingId]);

  if (days.length === 0) return <Empty description="Нет дней для графиков" />;

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <Card size="small" type="inner" title="Рост и тепло">
          <EChart option={growthOption} height={280} />
        </Card>
      </Col>
      <Col span={24}>
        <Card size="small" type="inner" title="Стресс и вода">
          <EChart option={stressOption} height={280} />
        </Card>
      </Col>
      <Col span={24}>
        <Card size="small" type="inner" title="Урожай">
          <EChart option={yieldOption} height={280} />
        </Card>
      </Col>
    </Row>
  );
};

// ── Сравнение прогонов ─────────────────────────────────────────────────────

interface GoalDiffRow {
  id: string;
  label: string;
  before: boolean | null;
  after: boolean | null;
  valueBefore: unknown;
  valueAfter: unknown;
}

const deltaNode = (
  before: number | null,
  after: number | null,
  digits = 1,
  higherIsBetter = true,
  /** false → показать дельту, но НЕ красить: прогоны несопоставимы. */
  causal = true,
): React.ReactNode => {
  const fmt = (v: number | null) => (v === null ? '—' : v.toFixed(digits));
  if (before === null && after === null) return <Text type="secondary">—</Text>;
  if (before === null || after === null) {
    return <Text>{fmt(before)} → {fmt(after)}</Text>;
  }
  const diff = after - before;
  const rounded = Number(diff.toFixed(digits));
  if (rounded === 0) {
    return (
      <Space size={4}>
        <Text>{fmt(before)} → {fmt(after)}</Text>
        <Text type="secondary">без изменений</Text>
      </Space>
    );
  }
  const improved = higherIsBetter ? rounded > 0 : rounded < 0;
  return (
    <Space size={4}>
      <Text>{fmt(before)} → {fmt(after)}</Text>
      <Text strong={causal} style={causal ? { color: improved ? BETTER : WORSE } : undefined} type={causal ? undefined : 'secondary'}>
        {rounded > 0 ? '+' : ''}{rounded.toFixed(digits)}
      </Text>
    </Space>
  );
};

const verdictTag = (passed: boolean | null) => {
  if (passed === null) return <Text type="secondary">—</Text>;
  return passed ? <Tag color="green">PASS</Tag> : <Tag color="red">FAIL</Tag>;
};

const RunComparison: React.FC<{
  pinned: PinnedRun;
  current: DebugRunResponse;
  plantingId?: string;
}> = ({ pinned, current, plantingId }) => {
  const before = pinned.run;
  const beforePlanting = finalPlanting(before, plantingId);
  const afterPlanting = finalPlanting(current, plantingId);

  const asNumber = (value: unknown): number | null => (typeof value === 'number' && Number.isFinite(value)
    ? value
    : null);

  // Прогоны, остановленные на разных днях, различаются прежде всего длиной, а не
  // правками: «высота +39.6» после переключения «до конца» — это лишние 25 дней
  // симуляции, а не эффект полива. Такие дельты показываем, но не красим.
  const sameWindow = before.cursorDate === current.cursorDate
    && before.daysAdvanced === current.daysAdvanced;

  const goalRows = useMemo<GoalDiffRow[]>(() => {
    const rows = new Map<string, GoalDiffRow>();
    before.goalsProgress.goals.forEach((goal) => {
      rows.set(goal.id, {
        id: goal.id,
        label: goal.label,
        before: goal.passed,
        after: null,
        valueBefore: goal.value,
        valueAfter: undefined,
      });
    });
    current.goalsProgress.goals.forEach((goal) => {
      const existing = rows.get(goal.id);
      if (existing) {
        existing.after = goal.passed;
        existing.valueAfter = goal.value;
        existing.label = goal.label || existing.label;
      } else {
        rows.set(goal.id, {
          id: goal.id,
          label: goal.label,
          before: null,
          after: goal.passed,
          valueBefore: undefined,
          valueAfter: goal.value,
        });
      }
    });
    return Array.from(rows.values());
  }, [before, current]);

  const goalColumns: ColumnsType<GoalDiffRow> = [
    { title: 'Цель', dataIndex: 'label' },
    {
      title: 'Значение',
      key: 'value',
      width: 220,
      render: (_, row) => deltaNode(asNumber(row.valueBefore), asNumber(row.valueAfter), 2, true, sameWindow),
    },
    { title: 'Закреплён', key: 'before', width: 110, render: (_, row) => verdictTag(row.before) },
    { title: 'Текущий', key: 'after', width: 110, render: (_, row) => verdictTag(row.after) },
    {
      title: 'Переход',
      key: 'shift',
      width: 140,
      render: (_, row) => {
        if (row.before === null) return <Tag color="blue">новая цель</Tag>;
        if (row.after === null) return <Tag>исчезла</Tag>;
        if (row.before === row.after) return <Text type="secondary">без изменений</Text>;
        return row.after
          ? <Tag color="green">FAIL→PASS</Tag>
          : <Tag color="red">PASS→FAIL</Tag>;
      },
    },
  ];

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <Text type="secondary">Закреплён: {pinned.label}</Text>
      {!sameWindow && (
        <Alert
          type="warning"
          showIcon
          message="Прогоны покрывают разные окна"
          description={`Закреплённый остановлен на ${before.cursorDate} (${before.daysAdvanced} дн.), текущий — на ${current.cursorDate} (${current.daysAdvanced} дн.). Разница по большей части объясняется длиной прогона, а не правками, поэтому дельты не подсвечены.`}
        />
      )}
      <Descriptions size="small" column={2} bordered>
        <Descriptions.Item label="Тир">
          {deltaNode(before.goalsProgress.scoreTier, current.goalsProgress.scoreTier, 0, true, sameWindow)}
        </Descriptions.Item>
        <Descriptions.Item label="Целей пройдено">
          {deltaNode(before.goalsProgress.passedCount, current.goalsProgress.passedCount, 0, true, sameWindow)}
          <Text type="secondary"> из {current.goalsProgress.total}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Дней прогнано">
          {deltaNode(before.daysAdvanced, current.daysAdvanced, 0, true, false)}
        </Descriptions.Item>
        <Descriptions.Item label="Курсор">
          {before.cursorDate} → {current.cursorDate}
        </Descriptions.Item>
        <Descriptions.Item label="Высота на финале, см">
          {deltaNode(asNumber(beforePlanting?.height), asNumber(afterPlanting?.height), 1, true, sameWindow)}
        </Descriptions.Item>
        <Descriptions.Item label="Здоровье на финале">
          {deltaNode(asNumber(beforePlanting?.healthScore), asNumber(afterPlanting?.healthScore), 1, true, sameWindow)}
        </Descriptions.Item>
        <Descriptions.Item label="GDD на финале">
          {deltaNode(asNumber(beforePlanting?.accumulatedGDD), asNumber(afterPlanting?.accumulatedGDD), 0, true, sameWindow)}
        </Descriptions.Item>
        <Descriptions.Item label="Собрано на финале, кг">
          {deltaNode(
            asNumber(beforePlanting?.cumulativeHarvestCollectedKg),
            asNumber(afterPlanting?.cumulativeHarvestCollectedKg),
            2,
            true,
            sameWindow,
          )}
        </Descriptions.Item>
      </Descriptions>
      <Table<GoalDiffRow>
        rowKey={(row) => row.id}
        size="small"
        pagination={false}
        columns={goalColumns}
        dataSource={goalRows}
        scroll={{ x: 800 }}
        locale={{ emptyText: 'У сценария нет целей' }}
      />
    </Space>
  );
};

// ── Эталон и ветки сравнения ───────────────────────────────────────────────

/**
 * Авторский шаг ленты, как он лежит в сиде (`comparison.referenceEvents` и
 * `comparison.branches[].eventsOverride`). День задан СМЕЩЕНИЕМ от старта, а не
 * датой, — поэтому сценарий не протухает.
 */
interface AuthoredStep {
  dateOffsetDays?: number;
  action: string;
  scenarioTarget?: { kind?: string; id?: string | number };
  target?: { kind?: string; id?: string | number };
  depthMm?: number;
  fertilizerKind?: string;
  quantityAffected?: number;
  quantityKg?: number;
}

interface AuthoredBranch {
  branchId?: string;
  id?: string;
  role?: string;
  label?: string;
  eventsOverride?: AuthoredStep[];
  events?: AuthoredStep[];
}

interface ContrastRow {
  key: string;
  label: string;
  role: string;
  run: DebugRunResponse;
  isReference: boolean;
}

const BRANCH_ROLE_TAG: Record<string, { color: string; text: string }> = {
  reference: { color: 'blue', text: 'эталон' },
  better: { color: 'green', text: 'лучше' },
  worse: { color: 'volcano', text: 'ошибка' },
  alternative: { color: 'purple', text: 'иначе' },
  baseline: { color: 'default', text: 'бездействие' },
};

/**
 * Авторский шаг → событие рантайма. Конвертер ОДИН на эталон и на ветки:
 * если конвертировать их по-разному, сравнение будет мерить разницу
 * конвертеров, а не разницу игры.
 */
const authoredStepToEvent = (
  step: AuthoredStep,
  template: ScenarioTemplate,
  prefix: string,
  index: number,
): DebugEvent => {
  const raw = step.scenarioTarget || step.target || {};
  const payload: Record<string, unknown> = {};
  if (step.depthMm != null) payload.wateringDepthMm = step.depthMm;
  if (step.fertilizerKind) payload.fertilizerKind = step.fertilizerKind;
  if (step.quantityAffected != null) payload.quantityAffected = step.quantityAffected;
  if (step.quantityKg != null) payload.quantityKg = step.quantityKg;
  return {
    id: `${prefix}-${index}`,
    type: step.action,
    date: addDays(template.worldSpec.startDate, Number(step.dateOffsetDays || 0)),
    target: raw.kind === 'planting' ? { plantingId: String(raw.id) } : { bedId: String(raw.id) },
    payload,
  };
};

const readComparison = (template: ScenarioTemplate | null) => {
  const comparison = (template?.comparison || null) as Record<string, unknown> | null;
  const referenceEvents = (comparison?.referenceEvents || []) as AuthoredStep[];
  const branches = (comparison?.branches || []) as AuthoredBranch[];
  const metrics = (comparison?.metrics || []) as string[];
  const referenceLabel = String(comparison?.referenceLabel || 'Эталонная игра');
  return { comparison, referenceEvents, branches, metrics, referenceLabel };
};

/**
 * Таблица «эталон и ветки против текущего прогона».
 *
 * Ветка хранит ДЕЙСТВИЯ, а не записанный результат, поэтому здесь она честно
 * переигрывается тем же ядром и в том же окне, что и прогон админа. Числа,
 * посчитанные когда-то и запечённые в шаблон, молча протухали бы от правки
 * техкарты — а так расхождение невозможно по построению.
 */
const ContrastTable: React.FC<{
  rows: ContrastRow[];
  metrics: string[];
  current: DebugRunResponse | null;
}> = ({ rows, metrics, current }) => {
  const reference = rows.find((row) => row.isReference) || null;
  const metricKeys = metrics.length ? metrics : ['healthScore'];

  const columns: ColumnsType<ContrastRow> = [
    {
      title: 'Как играли',
      dataIndex: 'label',
      render: (label: string, row) => (
        <Space direction="vertical" size={0}>
          <Text strong={row.isReference}>{label}</Text>
          <Tag color={BRANCH_ROLE_TAG[row.role]?.color || 'default'}>
            {BRANCH_ROLE_TAG[row.role]?.text || row.role}
          </Tag>
        </Space>
      ),
    },
    {
      title: 'Тир',
      width: 110,
      render: (_, row) => {
        const tier = row.run.goalsProgress.scoreTier;
        if (!tier) return <Text type="secondary">—</Text>;
        const ribbon: Record<number, { color: string; text: string }> = {
          3: { color: 'blue', text: 'синяя' },
          2: { color: 'red', text: 'красная' },
          1: { color: 'default', text: 'белая' },
        };
        return <Tag color={ribbon[tier]?.color}>{tier} · {ribbon[tier]?.text}</Tag>;
      },
    },
    {
      title: 'Цели',
      width: 90,
      render: (_, row) => `${row.run.goalsProgress.passedCount}/${row.run.goalsProgress.total}`,
    },
    ...metricKeys.map((key) => ({
      title: key,
      width: 150,
      render: (_: unknown, row: ContrastRow) => {
        const value = Number(row.run.metrics?.[key]);
        if (!Number.isFinite(value)) return <Text type="secondary">—</Text>;
        const refValue = reference ? Number(reference.run.metrics?.[key]) : NaN;
        const delta = Number.isFinite(refValue) && !row.isReference ? value - refValue : null;
        return (
          <Space size={4}>
            <Text>{value.toFixed(2)}</Text>
            {delta != null && Math.abs(delta) > 0.005 && (
              <Text type={delta > 0 ? 'success' : 'danger'}>
                ({delta > 0 ? '+' : ''}{delta.toFixed(2)})
              </Text>
            )}
          </Space>
        );
      },
    })),
  ];

  const withCurrent: ContrastRow[] = current
    ? [...rows, {
      key: 'current',
      label: 'Ваш прогон',
      role: 'alternative',
      run: current,
      isReference: false,
    }]
    : rows;

  return (
    <Table<ContrastRow>
      rowKey={(row) => row.key}
      size="small"
      pagination={false}
      columns={columns}
      dataSource={withCurrent}
      scroll={{ x: 700 }}
      rowClassName={(row) => (row.key === 'current' ? 'contrast-current-row' : '')}
    />
  );
};

// ── Страница ───────────────────────────────────────────────────────────────

const ScenarioDebugPage: React.FC = () => {
  const { data: templatesData, isFetching: templatesLoading } = useGetDebugTemplatesQuery();
  const [runScenario, { isLoading: running }] = useRunDebugScenarioMutation();

  const [slug, setSlug] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<DraftEvent[]>([]);
  const [result, setResult] = useState<DebugRunResponse | null>(null);
  const [pinned, setPinned] = useState<PinnedRun | null>(null);
  const [runToEnd, setRunToEnd] = useState(true);
  const [runToDay, setRunToDay] = useState(0);
  const [observedPlanting, setObservedPlanting] = useState<string | null>(null);
  const [observedBed, setObservedBed] = useState<string | null>(null);
  const [draftForm] = Form.useForm();

  /**
   * Экономика прохождения: черновой блок `economy` (накладывается на сид только
   * для прогона) и лог команд. Держим отдельно от `drafts`: уход и деньги — два
   * разных лога с разными правилами, и складывать их в один список значило бы
   * потерять это различие в первом же месте, где на них смотрят.
   */
  const [economyJson, setEconomyJson] = useState('');
  const [commandDrafts, setCommandDrafts] = useState<CommandDraft[]>([]);

  /**
   * Прогоны эталона и веток. Держим вместе с окном (`cursorDate`), в котором
   * они сняты: сравнивать прогоны, остановленные на разных днях, — это мерить
   * лишние дни симуляции, а не эффект игры (та же ловушка, что у закреплённого
   * прогона ниже).
   */
  const [contrast, setContrast] = useState<{ rows: ContrastRow[]; window: string } | null>(null);
  const [contrastLoading, setContrastLoading] = useState(false);

  const templates = useMemo(() => templatesData?.templates || [], [templatesData]);
  /** Читаем выбранный slug из ref, чтобы асинхронный ответ сверялся с текущим выбором. */
  const slugRef = useRef<string | null>(null);
  slugRef.current = slug;
  const template = useMemo(
    () => templates.find((t) => t.slug === slug) || null,
    [templates, slug],
  );

  const targets = useMemo(() => {
    if (!template) return { beds: [], plantings: [] };
    return {
      beds: template.worldSpec.beds.map((b) => ({
        value: b.id,
        label: `${b.name || b.id}${b.soilType ? ` (${b.soilType})` : ''}`,
      })),
      plantings: template.worldSpec.plantings.map((p) => ({ value: p.id, label: `${p.crop} · ${p.id}` })),
    };
  }, [template]);

  const economyOverlay = useMemo<Record<string, unknown> | null>(() => {
    if (!economyJson.trim()) return null;
    try {
      const parsed = JSON.parse(economyJson);
      return typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }, [economyJson]);

  /** Ключи предметов для выпадашки команд: из черновика, иначе из самого сида. */
  const economyItemIds = useMemo(() => {
    const source = economyOverlay ?? (template as { economy?: Record<string, unknown> } | null)?.economy ?? null;
    const catalog = (source as { catalog?: Array<{ id?: string }> } | null)?.catalog;
    return Array.isArray(catalog) ? catalog.map((item) => String(item?.id || '')).filter(Boolean) : [];
  }, [economyOverlay, template]);

  const crops = useMemo(
    () => Array.from(new Set((template?.worldSpec.plantings || []).map((p) => String(p.crop)).filter(Boolean))),
    [template],
  );

  const plantingId = observedPlanting || template?.worldSpec.plantings[0]?.id;
  const bedId = observedBed || template?.worldSpec.beds[0]?.id;

  const eventMarks = useMemo<EventMark[]>(() => {
    if (!template) return [];
    const byDate = new Map<string, string[]>();
    drafts.forEach((draft) => {
      const date = addDays(template.worldSpec.startDate, draft.day);
      const label = ACTION_OPTIONS.find((o) => o.value === draft.action)?.label || draft.action;
      const list = byDate.get(date) || [];
      if (!list.includes(label)) list.push(label);
      byDate.set(date, list);
    });
    return Array.from(byDate.entries()).map(([date, labels]) => ({ date, label: labels.join('+') }));
  }, [drafts, template]);

  const onSelectTemplate = (value: string) => {
    setSlug(value);
    setDrafts([]);
    setResult(null);
    setPinned(null);
    setContrast(null);
    setObservedPlanting(null);
    setObservedBed(null);
    setRunToEnd(true);
    setRunToDay(0);
    setEconomyJson('');
    setCommandDrafts([]);
    // Все семь семян используют одни и те же id (plot_1/bed_1/planting_1), поэтому
    // недосброшенная форма спокойно перенесёт день 26 из radish-rush в 7-дневный
    // first-sprout, и событие молча выпадет за окно реплея.
    draftForm.resetFields();
  };

  const onAddEvent = async () => {
    if (!template) return;
    const values = await draftForm.validateFields();
    const draft: DraftEvent = {
      key: (crypto as { randomUUID?: () => string }).randomUUID?.() || `evt-${Date.now()}-${Math.random()}`,
      day: values.day,
      action: values.action,
      targetId: values.targetId,
      depthMm: values.depthMm,
      fertilizerKind: values.fertilizerKind || undefined,
      quantityKg: values.quantityKg,
    };
    setDrafts((prev) => [...prev, draft].sort((a, b) => a.day - b.day));
  };

  /**
   * Прогоняет эталонную ленту и все ветки сценария в ТОМ ЖЕ окне, что и
   * последний прогон админа. Ленты лежат в сиде авторской формой
   * (`dateOffsetDays` + `scenarioTarget`), поэтому конвертируются здесь тем же
   * `authoredStepToEvent`, что и эталон, — иначе сравнение мерило бы разницу
   * конвертеров.
   */
  const onRunContrast = async () => {
    if (!template) return;
    const { referenceEvents, branches, referenceLabel } = readComparison(template);
    if (!referenceEvents.length && !branches.length) {
      message.info('У сценария нет ни эталонной ленты, ни веток — сравнивать не с чем');
      return;
    }
    const runSlug = template.slug;
    const cursorDate = result?.cursorDate
      || (runToEnd ? null : addDays(template.worldSpec.startDate, runToDay));

    const plan: Array<{ key: string; label: string; role: string; steps: AuthoredStep[]; isReference: boolean }> = [];
    if (referenceEvents.length) {
      plan.push({ key: 'reference', label: referenceLabel, role: 'reference', steps: referenceEvents, isReference: true });
    }
    branches.forEach((branch, index) => {
      const key = String(branch.branchId || branch.id || `branch_${index}`);
      plan.push({
        key,
        label: branch.label || key,
        role: String(branch.role || 'alternative'),
        // Пустая лента у роли `baseline` — законный «ничего не делал», а не описка.
        steps: branch.eventsOverride || branch.events || [],
        isReference: false,
      });
    });

    setContrastLoading(true);
    try {
      const rows: ContrastRow[] = [];
      for (const entry of plan) {
        // Последовательно, а не пачкой: прогонов единицы, а Promise.all тут
        // только усложнил бы сверку «сценарий не сменился».
        // eslint-disable-next-line no-await-in-loop
        const response = await runScenario({
          slug: runSlug,
          events: entry.steps.map((step, i) => authoredStepToEvent(step, template, entry.key, i)),
          // Ветки играются в ТОЙ ЖЕ экономике, что и прогон автора: иначе
          // разница чисел включала бы разницу прайса, а не разницу игры.
          economy: economyOverlay,
          cursorDate,
        }).unwrap();
        if (response.slug !== runSlug || runSlug !== slugRef.current) {
          message.info('Сценарий сменился во время сравнения — результат отброшен');
          return;
        }
        rows.push({ key: entry.key, label: entry.label, role: entry.role, run: response, isReference: entry.isReference });
      }
      setContrast({ rows, window: cursorDate || 'до конца' });
    } catch (err: unknown) {
      if (runSlug !== slugRef.current) return;
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || 'Не удалось прогнать ветки');
    } finally {
      setContrastLoading(false);
    }
  };

  const onRun = async () => {
    if (!template) return;
    // События позже курсора реплей отбрасывает БЕЗ диагностики (голый return в
    // ScenarioEventAdapter), поэтому предупредить может только клиент — иначе
    // «полив не подействовал» выглядит как поведение движка, а не как промах.
    if (!runToEnd) {
      const outside = drafts.filter((draft) => draft.day > runToDay);
      if (outside.length) {
        message.warning(
          `Вне окна прогона: ${outside.length} соб. (дни ${outside.map((d) => d.day).join(', ')}) — реплей их не применит`,
        );
      }
    }
    const runSlug = template.slug;
    try {
      const response = await runScenario({
        slug: runSlug,
        events: drafts.map((draft) => toDebugEvent(draft, template)),
        commands: commandDrafts.map(
          (draft, index) => toEconomyCommand(draft, template.worldSpec.startDate, index),
        ),
        economy: economyOverlay,
        cursorDate: runToEnd ? null : addDays(template.worldSpec.startDate, runToDay),
      }).unwrap();
      // Сценарий могли переключить, пока считалось. Id сущностей у всех семян
      // совпадают, так что чужая траектория отрисовалась бы как своя.
      if (response.slug !== runSlug || runSlug !== slugRef.current) {
        message.info('Сценарий сменился во время прогона — результат отброшен');
        return;
      }
      setResult(response);
    } catch (err: unknown) {
      if (runSlug !== slugRef.current) return;
      const detail = (err as { data?: { message?: string } })?.data?.message;
      message.error(detail || 'Прогон не удался');
    }
  };

  const onPin = () => {
    if (!result) return;
    const scope = result.terminal ? 'до конца' : `до ${result.cursorDate}`;
    setPinned({
      run: result,
      label: `${new Date().toLocaleString('ru-RU')} · ${drafts.length} соб. · ${scope}`,
    });
    message.success('Прогон закреплён — меняйте параметры и запускайте снова');
  };

  const trajectoryColumns: ColumnsType<DebugDay> = useMemo(() => {
    const eventDays = new Set(drafts.map((d) => d.day));
    return [
      {
        title: 'День',
        dataIndex: 'dayIndex',
        width: 70,
        render: (day: number) => (
          <Space size={4}>
            {day}
            {eventDays.has(day) && <Tag color="processing" style={{ marginInlineEnd: 0 }}>⚑</Tag>}
          </Space>
        ),
      },
      { title: 'Дата', dataIndex: 'simDate', width: 110 },
      {
        title: 'Погода',
        key: 'weather',
        width: 130,
        render: (_, day) => {
          const w = day.weather;
          if (!w) return '—';
          const rain = (w.precipitationMm ?? 0) > 0 ? ` ☔${w.precipitationMm}мм` : '';
          return `${w.tempMin}…${w.tempMax}°C${rain}`;
        },
      },
      {
        title: 'Фаза',
        key: 'phase',
        width: 120,
        render: (_, day) => phaseTag(plantingId ? day.plantings?.[plantingId]?.lifecyclePhase : undefined),
      },
      {
        title: 'GDD',
        key: 'gdd',
        width: 70,
        render: (_, day) => num(plantingId ? day.plantings?.[plantingId]?.accumulatedGDD : undefined, 0),
      },
      {
        title: 'Высота, см',
        key: 'height',
        width: 100,
        render: (_, day) => num(plantingId ? day.plantings?.[plantingId]?.height : undefined),
      },
      {
        title: 'Здоровье',
        key: 'health',
        width: 90,
        render: (_, day) => {
          const hp = plantingId ? day.plantings?.[plantingId]?.healthScore : undefined;
          if (typeof hp !== 'number') return '—';
          const color = hp >= 70 ? undefined : hp >= 40 ? '#d48806' : '#cf1322';
          return <Text style={color ? { color } : undefined}>{hp.toFixed(1)}</Text>;
        },
      },
      {
        title: 'Влага, %',
        key: 'moist',
        width: 90,
        render: (_, day) => num(bedId ? day.beds?.[bedId]?.moisturePct : undefined),
      },
      {
        title: 'Спелое/зелёное, кг',
        key: 'fruit',
        width: 140,
        render: (_, day) => {
          const p = plantingId ? day.plantings?.[plantingId] : undefined;
          if (!p || (p.fruitRipeKg == null && p.fruitUnripeKg == null)) return '—';
          return `${num(p.fruitRipeKg, 2)} / ${num(p.fruitUnripeKg, 2)}`;
        },
      },
      {
        title: 'Собрано, кг',
        key: 'collected',
        width: 100,
        render: (_, day) => num(plantingId ? day.plantings?.[plantingId]?.cumulativeHarvestCollectedKg : undefined, 2),
      },
    ];
  }, [drafts, plantingId, bedId]);

  const draftColumns: ColumnsType<DraftEvent> = [
    { title: 'День', dataIndex: 'day', width: 70 },
    {
      title: 'Действие',
      dataIndex: 'action',
      render: (action: string) => ACTION_OPTIONS.find((o) => o.value === action)?.label || action,
    },
    { title: 'Цель', dataIndex: 'targetId' },
    {
      title: 'Параметры',
      key: 'params',
      render: (_, d) => {
        const parts: string[] = [];
        if (d.depthMm) parts.push(`${d.depthMm} мм`);
        if (d.fertilizerKind) parts.push(d.fertilizerKind);
        if (d.quantityKg) parts.push(`${d.quantityKg} кг`);
        return parts.join(', ') || '—';
      },
    },
    {
      title: '',
      key: 'remove',
      width: 60,
      render: (_, d) => (
        <Button
          size="small"
          danger
          type="text"
          onClick={() => setDrafts((prev) => prev.filter((x) => x.key !== d.key))}
        >
          ✕
        </Button>
      ),
    },
  ];

  const selectedAction = Form.useWatch('action', draftForm);

  return (
    <MainLayout>
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card title="Сценарная отладка" loading={templatesLoading}>
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
              message="Dry-run канонического сценария тем же ядром, что играет Unity и реплеит сервер. Ничего не сохраняется — можно спамить прогоны при калибровке целей/погоды."
            />
            <Space wrap>
              <Select
                style={{ minWidth: 360 }}
                placeholder="Выберите сценарий"
                value={slug}
                onChange={onSelectTemplate}
                options={templates.map((t) => ({
                  value: t.slug,
                  label: `${t.title} (${t.slug}, ${t.kind}, ${t.durationDays}д)`,
                }))}
              />
              {template && (
                <Text type="secondary">
                  {template.worldSpec.startDate} → {template.terminalDate}
                </Text>
              )}
            </Space>

            {template && (
              <Descriptions size="small" column={2} style={{ marginTop: 16 }}>
                <Descriptions.Item label="Интро">{template.narrative?.intro || '—'}</Descriptions.Item>
                <Descriptions.Item label="Подсказка">{template.narrative?.hint || '—'}</Descriptions.Item>
              </Descriptions>
            )}
          </Card>
        </Col>

        {template && (
          <Col span={24}>
            <Card
              size="small"
              title="События игрока"
              extra={(
                <Space wrap>
                  <Checkbox checked={runToEnd} onChange={(e) => setRunToEnd(e.target.checked)}>
                    до конца
                  </Checkbox>
                  <Text type="secondary">Прогнать до дня</Text>
                  <InputNumber
                    min={0}
                    max={template.durationDays}
                    value={runToDay}
                    disabled={runToEnd}
                    onChange={(value) => setRunToDay(typeof value === 'number' ? value : 0)}
                    style={{ width: 90 }}
                  />
                  {!runToEnd && (
                    <Text type="secondary">→ {addDays(template.worldSpec.startDate, runToDay)}</Text>
                  )}
                  <Button type="primary" onClick={onRun} loading={running}>
                    Прогнать сценарий
                  </Button>
                </Space>
              )}
            >
              <Form form={draftForm} layout="inline" style={{ marginBottom: 12 }} initialValues={{ day: 1, action: 'watering', depthMm: 15 }}>
                <Form.Item name="day" label="День" rules={[{ required: true }]}>
                  <InputNumber min={0} max={template.durationDays} style={{ width: 80 }} />
                </Form.Item>
                <Form.Item name="action" label="Действие" rules={[{ required: true }]}>
                  <Select style={{ width: 140 }} options={ACTION_OPTIONS} />
                </Form.Item>
                <Form.Item name="targetId" label="Цель" rules={[{ required: true, message: 'цель' }]}>
                  <Select
                    style={{ width: 220 }}
                    options={selectedAction === 'harvest' ? targets.plantings : targets.beds}
                    placeholder={selectedAction === 'harvest' ? 'посадка' : 'грядка'}
                  />
                </Form.Item>
                {selectedAction === 'watering' && (
                  <Form.Item name="depthMm" label="мм">
                    <InputNumber min={1} max={60} style={{ width: 80 }} />
                  </Form.Item>
                )}
                {selectedAction === 'fertilizing' && (
                  <Form.Item name="fertilizerKind" label="Рецепт">
                    <Select style={{ width: 280 }} options={FERTILIZER_KINDS} allowClear placeholder="NPK (дефолт)" />
                  </Form.Item>
                )}
                {selectedAction === 'harvest' && (
                  <Form.Item name="quantityKg" label="кг (пусто = всё спелое)">
                    <InputNumber min={0.01} step={0.1} style={{ width: 110 }} />
                  </Form.Item>
                )}
                <Form.Item>
                  <Button onClick={onAddEvent}>Добавить</Button>
                </Form.Item>
              </Form>
              <Table<DraftEvent>
                rowKey={(d) => d.key}
                size="small"
                columns={draftColumns}
                dataSource={drafts}
                pagination={false}
                locale={{ emptyText: 'Без событий — «ленивый» прогон' }}
              />
            </Card>
          </Col>
        )}

        {template && (
          <Col span={24}>
            <ScenarioEconomyComposer
              startDate={template.worldSpec.startDate}
              durationDays={template.durationDays}
              crops={crops}
              beds={targets.beds}
              itemIds={economyItemIds}
              economyJson={economyJson}
              onEconomyJsonChange={setEconomyJson}
              commands={commandDrafts}
              onCommandsChange={setCommandDrafts}
            />
          </Col>
        )}

        {result && (
          <>
            {result.diagnostics && result.diagnostics.length > 0 && (
              <Col span={24}>
                <DiagnosticsPanel diagnostics={result.diagnostics} />
              </Col>
            )}

            <Col span={24}>
              <GoalsPanel
                result={result}
                template={template}
                extra={(
                  <Space wrap>
                    {pinned ? (
                      <>
                        <Button icon={<PushpinFilled />} onClick={onPin}>
                          Перезакрепить текущий
                        </Button>
                        <Button danger type="text" onClick={() => setPinned(null)}>
                          Открепить
                        </Button>
                      </>
                    ) : (
                      <Button icon={<PushpinOutlined />} onClick={onPin}>
                        Закрепить прогон
                      </Button>
                    )}
                  </Space>
                )}
              />
            </Col>

            {(result.economy || (result.economySpecErrors?.length ?? 0) > 0) && (
              <Col span={24}>
                <ScenarioEconomyPanel
                  economy={result.economy}
                  specErrors={result.economySpecErrors}
                />
              </Col>
            )}

            <Col span={24}>
              <Card
                size="small"
                title="Эталон и ветки сравнения"
                extra={(
                  <Button loading={contrastLoading} onClick={onRunContrast}>
                    {contrast ? 'Пересчитать' : 'Прогнать эталон и ветки'}
                  </Button>
                )}
              >
                {!contrast ? (
                  <Text type="secondary">
                    Это то, что увидит игрок после финала: как сценарий играет тот, кто понял урок,
                    и что бывает при типичных ошибках. Ветки хранят действия, а не записанный
                    результат, — они переигрываются тем же ядром и в том же окне, что ваш прогон.
                  </Text>
                ) : (
                  <Space direction="vertical" size={8} style={{ width: '100%' }}>
                    {contrast.window !== (result.cursorDate || 'до конца') && (
                      <Alert
                        type="warning"
                        showIcon
                        message="Ветки сняты в другом окне"
                        description={`Ветки прогнаны до ${contrast.window}, текущий прогон — до ${result.cursorDate}. Разница чисел включает лишние дни симуляции. Пересчитайте.`}
                      />
                    )}
                    <ContrastTable
                      rows={contrast.rows}
                      metrics={readComparison(template).metrics}
                      current={result}
                    />
                  </Space>
                )}
              </Card>
            </Col>

            {pinned && (
              <Col span={24}>
                <Card size="small" title="Сравнение прогонов (закреплённый → текущий)">
                  {pinned.run === result ? (
                    <Text type="secondary">
                      Закреплён этот же прогон. Поменяйте события или день остановки и запустите снова.
                    </Text>
                  ) : (
                    <RunComparison pinned={pinned} current={result} plantingId={plantingId} />
                  )}
                </Card>
              </Col>
            )}

            <Col span={24}>
              <Card
                size="small"
                title={(
                  <Space wrap size={6}>
                    <Text strong>Траектория по дням ({result.days.length})</Text>
                    <Text type="secondary">посадка {plantingId || '—'} · грядка {bedId || '—'}</Text>
                  </Space>
                )}
                extra={(
                  <Space wrap>
                    <Select
                      style={{ minWidth: 220 }}
                      value={plantingId}
                      onChange={(value) => setObservedPlanting(value ?? null)}
                      options={targets.plantings}
                      placeholder="посадка"
                    />
                    <Select
                      style={{ minWidth: 200 }}
                      value={bedId}
                      onChange={(value) => setObservedBed(value ?? null)}
                      options={targets.beds}
                      placeholder="грядка"
                    />
                  </Space>
                )}
              >
                <Table<DebugDay>
                  rowKey={(d) => d.dayIndex}
                  size="small"
                  columns={trajectoryColumns}
                  dataSource={result.days}
                  pagination={false}
                  scroll={{ x: 1000 }}
                />
              </Card>
            </Col>

            <Col span={24}>
              <Card size="small" title="Графики">
                <TrajectoryCharts
                  days={result.days}
                  plantingId={plantingId}
                  bedId={bedId}
                  eventMarks={eventMarks}
                />
              </Card>
            </Col>
          </>
        )}

        {!template && !templatesLoading && (
          <Col span={24}>
            <Empty description="Выберите сценарий для отладки" />
          </Col>
        )}
      </Row>
    </MainLayout>
  );
};

export default ScenarioDebugPage;
